import {CONTENT_OFFSET} from "../domain/ContentOffset";
import {FOLDER_TYPE, NODE_LEVEL} from "../domain/HierarchyConstants";

export const shouldGroupExpand = (rowGroup, rowIdsEligibleToExpand) => rowIdsEligibleToExpand.has(rowGroup.id) || false;

export const flattenHierarchy = (item) => {
    let flattenedNodes = [{
        id: item.id,
        name: item.name,
        type: item.type,
        hierarchy: item.hierarchy
    }];

    if (item.children && item.children.length > 0) {
        item.children.forEach((child) => {
            const flattenedChildren = flattenHierarchy(child);
            flattenedNodes = [...flattenedNodes, ...flattenedChildren];
        });
    }

    return flattenedNodes;
}

export const buildRowObject = (flattenedHierarchy, data, additionalProperties) => {
    const content = flattenedHierarchy.find(hierarchy => hierarchy.id === data.id);

    const defaultRowObject = {
        id: data.id,
        originalId: data.originalId,
        primaryColor: data.primaryColor,
        displayName: data.text,
        contentNameHierarchy: content ? content.name : [data.text],
        hierarchy: content ? content.hierarchy : [data.id],
        nodeType: data.type,
        folderType: data.folderType,
    };

    return { ...defaultRowObject, ...additionalProperties };
}

export const buildContentHierarchyPath = (contentFolders, treeNode, serviceType, childrenId = null) => {
    const hierarchyNodeIds = [];
    let currentItem = treeNode;

    while (currentItem !== undefined && currentItem.parent !== 0) {
        const parentItem = contentFolders.find(folder => folder.id === currentItem.parent);
        hierarchyNodeIds.unshift(currentItem.id);
        currentItem = parentItem;
    }

    hierarchyNodeIds.unshift(serviceType);
    if (childrenId) {
        hierarchyNodeIds.push(childrenId);
    }

    return hierarchyNodeIds;
}

export const buildContentFolders = (data, contentInfos) => {

    return data.contentFolders.map(item => {
        const contentInfoChildren = contentInfos
            .filter(contentInfo => contentInfo.parent === item.id + (item.serviceType.id * CONTENT_OFFSET.CONTENT_FOLDER_ID_OFFSET))
            .map(contentInfo => contentInfo.originalId);

        const contentFolderChildren = data.contentFolders
            .filter(contentFolder => contentFolder.parentFolderId === item.id)
            .map(contentFolder => contentFolder.originalId);

        const parentServiceType = data.serviceTypes.find((serviceType) => serviceType.id === item.serviceType.id);
        const contentFolderOffsetId = item.id + (item.serviceType.id * CONTENT_OFFSET.CONTENT_FOLDER_ID_OFFSET);
        const contentFolderParentId = item.parentFolderId
              ? item.parentFolderId + (item.serviceType.id * CONTENT_OFFSET.CONTENT_FOLDER_ID_OFFSET)
              : item.serviceType.id;

        return {
            id: contentFolderOffsetId,
            originalId: item.id,
            text: item.name,
            parent: contentFolderParentId,
            type: NODE_LEVEL.BRANCH,
            childrenFromContentInfos: contentInfoChildren,
            childrenFromContentFolders: contentFolderChildren,
            primaryColor: parentServiceType ? parentServiceType.primaryColor : null,
            parentServiceTypeId: parentServiceType ? parentServiceType.id : null,
            folderType: FOLDER_TYPE.CONTENT_FOLDER,
        }
    });
}

export const buildServiceTypesHierarchy = (data, contentFolders, contentInfos) => {

    return data.serviceTypes.map(item => {
        const contentInfoChildren = contentInfos
            .filter(contentInfo => contentInfo.parent === item.id)
            .map(contentInfo => contentInfo.originalId);
        const contentFolderChildren = contentFolders
            .filter(contentFolder => contentFolder.parent === item.id)
            .map(contentFolder => contentFolder.childrenFromContentInfos);

        return {
            id: item.id,
            originalId: item.id,
            text: item.description,
            parent: 0,
            type: NODE_LEVEL.ROOT,
            primaryColor: item.primaryColor,
            childrenFromContentFolders: contentFolderChildren,
            childrenFromContentInfos: contentInfoChildren,
            parentServiceTypeId: item.id,
            folderType: FOLDER_TYPE.SERVICE_TYPE,
        }
    });
}

export const buildContentInfoHierarchyForFolder = (contentInfos, contentFolders, currentFolder, currentServiceType) => {

    return contentInfos.filter(info => info.parent === currentFolder.id)
        .map(info => ({
            id: info.id,
            type: info.type,
            name: buildContentHierarchyPath(contentFolders, currentFolder, currentServiceType.text, info.text),
            hierarchy: buildContentHierarchyPath(contentFolders, currentFolder, currentServiceType.id, info.id),
            originalId: info.originalId
        }));
}

export const buildContentInfoRecursively = (contentInfos, contentFolders, folderId, currentHierarchy, currentHierarchyId, currentServiceType) => {
    const children = [];

    contentFolders.filter(subFolder => subFolder.parent === folderId)
        .forEach(subFolder => {
            let childrenFromContentInfos = buildContentInfoHierarchyForFolder(contentInfos, contentFolders, subFolder, currentServiceType);
            children.push({
                id: subFolder.id,
                type: subFolder.type,
                name: [...currentHierarchy, subFolder.text],
                hierarchy: [...currentHierarchyId, subFolder.id],
                children: [
                    ...childrenFromContentInfos,
                    ...buildContentInfoRecursively(
                      contentInfos,
                      contentFolders,
                      subFolder.id,
                      [...currentHierarchy, subFolder.text],
                      [...currentHierarchyId, subFolder.id],
                      currentServiceType
                    )]
            });
        });

    return children;
};

export const buildServiceTypeHierarchyMap = (serviceTypes, contentFolders, contentInfos) => {
    return serviceTypes.map(serviceType => {
        const contentInfoHierarchy = contentInfos.filter(info => info.parent === serviceType.id)
            .map(info => ({
                id: info.id,
                type: info.type,
                name: [serviceType.text, info.text],
                hierarchy: [serviceType.id, info.id],
                originalId: info.originalId
            }));

        const contentFolderHierarchy = buildCurrentContentFolderHierarchy(contentFolders, contentInfos, serviceType);

        return {
            id: serviceType.id,
            type: serviceType.type,
            name: [serviceType.text],
            hierarchy: [serviceType.id],
            children: [...contentFolderHierarchy, ...contentInfoHierarchy]
        };
    });
}

export const buildCurrentContentFolderHierarchy = (contentFolders, contentInfos, currentServiceType) => {
    return contentFolders.filter(folder => folder.parent === currentServiceType.id).map(folder => {

        const contentInfoChildren = buildContentInfoHierarchyForFolder(contentInfos, contentFolders, folder, currentServiceType);
        const contentInfosFromContentFolders = buildContentInfoRecursively(
          contentInfos,
          contentFolders,
          folder.id,
          [currentServiceType.text, folder.text],
          [currentServiceType.id, folder.id],
          currentServiceType
        );

        return {
            id: folder.id,
            type: folder.type,
            name: [currentServiceType.text, folder.text],
            hierarchy: [currentServiceType.id, folder.id],
            children: [...contentInfoChildren, ...contentInfosFromContentFolders]
        };
    });
}
